/**
 * Compliance report download / preview Section.
 *
 * Extracted from `AdminMerchantDetail.jsx` in Brief 3.5 PR 6.1 — pure
 * mechanical refactor of code that landed in PR 6. No behavior change.
 *
 * Two actions:
 *   • View HTML report — opens the regulator-ready HTML in a new tab.
 *   • Download PDF     — fetches the branded PDF and offers it as a file.
 *
 * Both routes are audit-logged server-side. We fetch as blobs because
 * the endpoints require an Authorization header — window.open(url)
 * cannot attach auth, so we materialise an object URL.
 */
import { useState } from "react";
import { ExternalLink, FileText, Loader2 } from "lucide-react";
import api from "../../../lib/api";
import { Section, fmtRelative } from "./primitives";


function LastViewedLine({ lastRender }) {
  if (!lastRender || !lastRender.timestamp) return null;
  // Brief 3.5 PR 7-D — distinguish PDF download (higher commitment,
  // about to share externally) from HTML view (quick scan). Two
  // reviewers on the same merchant benefit from knowing which.
  const verb = lastRender.format === "pdf" ? "pulled the PDF" : "viewed the report";
  return (
    <div
      className="mb-3 text-[12px] text-zinc-500"
      data-testid="compliance-report-last-viewed"
    >
      <span className="text-zinc-300">{lastRender.actor_name}</span>
      {" "}{verb} · {fmtRelative(lastRender.timestamp) || "recently"}
    </div>
  );
}


export default function ComplianceReportActions({ merchantId, legalName, lastRender }) {
  const [busy, setBusy] = useState(null); // 'html' | 'pdf' | null
  const filename = (legalName || "merchant").replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase().slice(0, 60);

  const openHtml = async () => {
    setBusy("html");
    try {
      const r = await api.get(
        `/admin/merchants/${merchantId}/compliance-report.html`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        // Popup blocked — fall back to forced navigation.
        const a = document.createElement("a");
        a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert(`Couldn't open the HTML report: ${e?.response?.status || "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const r = await api.get(
        `/admin/merchants/${merchantId}/compliance-report.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `compliance-report-${filename}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert(`Couldn't download the PDF: ${e?.response?.status || "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Section
      icon={FileText}
      title="Compliance report"
      subtitle="Point-in-time snapshot for sign-off & archive."
      testId="merchant-section-compliance-report"
    >
      <LastViewedLine lastRender={lastRender} />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={openHtml}
          disabled={busy !== null}
          data-testid="compliance-report-open-html"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] px-3.5 py-1.5 text-[12.5px] text-zinc-200 hover:border-cyan-400/40 hover:bg-cyan-400/[0.06] hover:text-cyan-100 transition-colors disabled:opacity-50"
        >
          {busy === "html" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
          View HTML report
        </button>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={busy !== null}
          data-testid="compliance-report-download-pdf"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] px-3.5 py-1.5 text-[12.5px] text-zinc-200 hover:border-violet-400/40 hover:bg-violet-400/[0.06] hover:text-violet-100 transition-colors disabled:opacity-50"
        >
          {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          Download PDF
        </button>
      </div>
      <div className="mt-2 text-[11.5px] text-zinc-500">
        Includes executive summary, document checklist, open findings & sign-off block. Each export is audit-logged.
      </div>
    </Section>
  );
}
